/* eslint-disable react/no-unstable-nested-components */
import { useEffect, useState } from "react";
import Layout from "../components/Layout";
import SortableGrid from "../components/SortableGrid";
import api from "../lib/api";
import { fmtCur } from "../lib/format";
import { toast } from "sonner";
import { useAuth } from "../contexts/AuthContext";
import { Users as UsersIcon, Gear, X, EyeSlash, ArrowCounterClockwise, Plus, Trash } from "@phosphor-icons/react";

const DUE_MEANS = [
  { v: "delivery_date", l: "Customer-site delivery date" },
  { v: "dock_date", l: "Dock / port arrival date" },
  { v: "ex_factory_date", l: "Ex-factory date" },
  { v: "port_arrival", l: "Port of discharge arrival" },
];
const RESP = [
  { v: "customer_pickup", l: "Customer picks up (ex-works)" },
  { v: "we_deliver", l: "We deliver (door-to-door)" },
];
const CURRENCIES = ["USD", "INR", "GBP", "EUR", "CAD", "AUD"];
const PLANNING_MODELS = [
  { v: "firm_po_forecast", l: "Firm PO + Forecast", short: "PO + Forecast" },
  { v: "firm_po_ams", l: "Firm PO + AMS (6-mo avg)", short: "PO + AMS" },
  { v: "firm_po_only", l: "Firm PO only", short: "Firm PO only" },
];

export default function Customers() {
  const { user } = useAuth();
  const isAdmin = ["admin", "super_admin"].includes(user?.role);
  const [customers, setCustomers] = useState([]);
  const [editing, setEditing] = useState(null);
  const [creating, setCreating] = useState(false);
  const [showHidden, setShowHidden] = useState(false);

  const load = () => api.get(`/customers${showHidden ? "?include_hidden=true" : ""}`).then((r) => setCustomers(r.data));
  useEffect(() => { load(); }, [showHidden]); // eslint-disable-line react-hooks/exhaustive-deps

  const setArchived = async (c, archived) => {
    try {
      await api.put(`/customers/${c.id}/archive`, { archived });
      toast.success(archived ? `Hidden "${c.name}"` : `Restored "${c.name}"`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Action failed");
    }
  };

  const deleteCustomer = async (c) => {
    if (!window.confirm(`Permanently delete customer "${c.name}" (${c.ms_pcode})? This cannot be undone.`)) return;
    try {
      await api.delete(`/customers/${c.id}`);
      toast.success(`Deleted "${c.name}"`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Delete failed");
    }
  };

  const columns = [
    { key: "name", header: "Customer",
      render: (c) => (
        <span style={c.archived ? { opacity: 0.6 } : undefined}>
          <strong>{c.name}</strong>
          {c.archived && <span className="jde-badge closed" style={{ marginLeft: 6, fontSize: 9 }}>Hidden</span>}
        </span>
      ) },
    { key: "ms_pcode", header: "ERP Code",
      render: (c) => (
        <span className="text-mono" style={{ fontSize: 11 }}>
          {c.ms_pcode || <span className="muted">—</span>}
          {c.manual && <span className="jde-badge" style={{ marginLeft: 6, fontSize: 8.5, background: "#DCFCE7", color: "#166534" }}>MANUAL</span>}
        </span>
      ) },
    { key: "currency", header: "Currency",
      render: (c) => c.currency ? <span className="text-mono" style={{ fontSize: 11 }}>{c.currency}</span> : <span className="muted">—</span> },
    { key: "planning_model", header: "Planning Model",
      accessor: (c) => c.planning_model || "firm_po_only",
      render: (c) => {
        // null / unset is treated as "Firm PO only" everywhere — there is no unconfigured state.
        const m = PLANNING_MODELS.find((x) => x.v === c.planning_model)
          || PLANNING_MODELS.find((x) => x.v === "firm_po_only");
        return <span className="jde-badge confirmed" data-testid={`plan-model-${c.id}`}>{m.short}</span>;
      } },
    { key: "ams_6mo_units", header: "AMS (u/mo)", align: "right",
      accessor: (c) => c.ams_6mo_units || 0,
      render: (c) => c.ams_6mo_units
        ? <span className="text-mono" style={{ fontSize: 11 }}>{Number(c.ams_6mo_units).toLocaleString()}</span>
        : <span className="muted">—</span> },
    { key: "email", header: "Email",
      render: (c) => <span className="text-mono" style={{ fontSize: 11 }}>{c.email || <span className="muted">—</span>}</span> },
    { key: "po_due_date_means", header: "PO Date Means",
      accessor: (c) => c.date_config?.po_due_date_means || "delivery_date",
      render: (c) => (DUE_MEANS.find((x) => x.v === (c.date_config?.po_due_date_means || "delivery_date"))?.l || "—") },
    { key: "ex_factory_offset_days", header: "Ex-Fact Offset (d)", align: "right",
      accessor: (c) => c.date_config?.ex_factory_offset_days || 0 },
    { key: "transit_days", header: "Transit (d)", align: "right",
      accessor: (c) => c.date_config?.transit_days || 0 },
    { key: "delivery_responsibility", header: "Responsibility",
      accessor: (c) => c.date_config?.delivery_responsibility || "customer_pickup",
      render: (c) => <span className={`jde-badge ${c.date_config?.delivery_responsibility === "we_deliver" ? "confirmed" : "closed"}`}>
        {c.date_config?.delivery_responsibility === "we_deliver" ? "We deliver" : "Pickup"}
      </span> },
    { key: "total_open_orders", header: "Open POs", align: "right" },
    { key: "total_open_value", header: "Open Value", align: "right",
      accessor: (c) => c.total_open_value || 0,
      render: (c) => {
        const ccys = Object.entries(c.open_value_by_ccy || {}).filter(([, v]) => v);
        if (ccys.length === 0) return <span className="muted">—</span>;
        return (
          <div data-testid={`cust-open-value-${c.id}`}>
            {ccys.map(([cc, v]) => (
              <div key={cc} className="text-mono" style={{ fontSize: 11 }}>{fmtCur(v, cc)}</div>
            ))}
          </div>
        );
      } },
    { key: "actions", header: "Actions", sortable: false,
      render: (c) => (
        <div style={{ display: "flex", gap: 6 }} onClick={(e) => e.stopPropagation()}>
          <button className="jde-btn jde-btn-sm" onClick={() => setEditing(c)} data-testid={`config-${c.id}`}>
            <Gear size={11} weight="bold" /> Edit
          </button>
          {isAdmin && (c.archived
            ? <button className="jde-btn jde-btn-sm" onClick={() => setArchived(c, false)} data-testid={`restore-${c.id}`}>
                <ArrowCounterClockwise size={11} weight="bold" /> Restore
              </button>
            : <button className="jde-btn jde-btn-sm" onClick={() => setArchived(c, true)} data-testid={`hide-${c.id}`}
                title="Hide from the customer master & all dropdowns">
                <EyeSlash size={11} weight="bold" /> Hide
              </button>)}
          {isAdmin && c.manual && (
            <button className="jde-btn jde-btn-sm" onClick={() => deleteCustomer(c)} data-testid={`delete-${c.id}`}
              title="Permanently delete this manually-added customer" style={{ color: "#B91C1C" }}>
              <Trash size={11} weight="bold" /> Delete
            </button>
          )}
        </div>
      ) },
  ];

  return (
    <Layout breadcrumb={["Business Config"]}>
      <div className="jde-toolbar" style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <span style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <UsersIcon size={14} weight="bold" />
          <span style={{ fontWeight: 700, fontSize: 13 }}>BUSINESS CONFIG · CUSTOMERS, DATE &amp; PLANNING MODEL</span>
        </span>
        {isAdmin && (
          <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
            <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12, cursor: "pointer" }} data-testid="toggle-show-hidden">
              <input type="checkbox" checked={showHidden} onChange={(e) => setShowHidden(e.target.checked)} />
              Show hidden
            </label>
            <button className="jde-btn jde-btn-primary jde-btn-sm" onClick={() => setCreating(true)} data-testid="add-customer-btn">
              <Plus size={11} weight="bold" /> Add Customer
            </button>
          </div>
        )}
      </div>

      <div className="jde-panel" style={{ marginTop: 12 }}>
        <SortableGrid pageSize={100}
          columns={columns}
          rows={customers}
          getRowKey={(c) => c.id}
          onRowClick={(c) => setEditing(c)}
          defaultSort={{ key: "total_open_orders", dir: "desc" }}
          searchPlaceholder="Search customer, email…"
          testid="customers-grid"
          emptyMessage="No customers yet — auto-created from incoming POs."
          statusBar={(v, all) => (<span>Customers: {v.length}{v.length !== all.length ? ` of ${all.length}` : ""}</span>)}
        />
      </div>

      {editing && <ConfigModal customer={editing} isAdmin={isAdmin} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}
      {creating && <CreateModal onClose={() => setCreating(false)} onSaved={() => { setCreating(false); load(); }} />}
    </Layout>
  );
}

function ConfigModal({ customer, isAdmin, onClose, onSaved }) {
  const cfg = customer.date_config || {};
  const [form, setForm] = useState({
    name: customer.name || "",
    ms_pcode: customer.ms_pcode || "",
    currency_code: customer.currency || "",
    planning_model: customer.planning_model || "firm_po_only",
    po_due_date_means: cfg.po_due_date_means || "delivery_date",
    ex_factory_offset_days: cfg.ex_factory_offset_days ?? 0,
    transit_days: cfg.transit_days ?? 0,
    delivery_responsibility: cfg.delivery_responsibility || "customer_pickup",
    notes: cfg.notes || "",
  });
  const [busy, setBusy] = useState(false);

  const save = async () => {
    setBusy(true);
    try {
      // Planning model — editable by admin + manager (dedicated endpoint).
      await api.put(`/customers/${customer.id}/planning-model`, { planning_model: form.planning_model || null });
      // Core master fields + date config — admin only.
      if (isAdmin) {
        await api.put(`/customers/${customer.id}`, {
          name: form.name,
          ms_pcode: form.ms_pcode,
          currency_code: form.currency_code,
          date_config: {
            po_due_date_means: form.po_due_date_means,
            delivery_responsibility: form.delivery_responsibility,
            notes: form.notes,
            ex_factory_offset_days: Number(form.ex_factory_offset_days) || 0,
            transit_days: Number(form.transit_days) || 0,
          },
        });
      }
      toast.success("Customer saved");
      onSaved();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally { setBusy(false); }
  };

  return (
    <div onClick={onClose} style={{
      position: "fixed", inset: 0, background: "rgba(11, 18, 32, 0.55)",
      display: "flex", alignItems: "center", justifyContent: "center", zIndex: 100, padding: 20,
    }}>
      <div onClick={(e) => e.stopPropagation()} style={{
        background: "#fff", borderRadius: 10, width: "100%", maxWidth: 560,
        boxShadow: "0 24px 60px rgba(0,0,0,0.3)", overflow: "hidden",
      }} data-testid="customer-config-modal">
        <div className="jde-panel-header">
          <span>Edit Customer · {customer.name}</span>
          <button className="jde-btn jde-btn-sm" onClick={onClose}><X size={11} weight="bold" /></button>
        </div>
        <div className="jde-panel-body">
          <div style={{ marginBottom: 12, padding: "10px 12px", background: "#f0fdfa", border: "1px solid #99f6e4", borderRadius: 8 }}>
            <label className="jde-label" style={{ color: "#0f766e" }}>Production Planning Model</label>
            <select className="jde-select" value={form.planning_model}
              onChange={(e) => setForm({ ...form, planning_model: e.target.value })} data-testid="cfg-planning-model">
              {PLANNING_MODELS.map((x) => <option key={x.v} value={x.v}>{x.l}</option>)}
            </select>
            <div className="muted" style={{ fontSize: 10.5, marginTop: 5 }}>
              How we plan production for this customer. Ingested by the downstream app via the integration feed.
              {customer.ams_6mo_units ? ` · Current AMS ≈ ${Number(customer.ams_6mo_units).toLocaleString()} units/mo (last 6 mo).` : ""}
            </div>
          </div>
          {!isAdmin && (
            <div className="muted" style={{ fontSize: 11, marginBottom: 10 }}>
              You can set the planning model. Other customer fields are admin-only.
            </div>
          )}
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
            <div>
              <label className="jde-label">Customer Name</label>
              <input className="jde-input" value={form.name} disabled={!isAdmin}
                onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="cfg-name" />
            </div>
            <div>
              <label className="jde-label">ERP Code (Pcode)</label>
              <input className="jde-input text-mono" value={form.ms_pcode} disabled={!isAdmin}
                onChange={(e) => setForm({ ...form, ms_pcode: e.target.value.toUpperCase() })} data-testid="cfg-pcode" />
            </div>
            <div>
              <label className="jde-label">Default Currency</label>
              <select className="jde-select" value={form.currency_code} disabled={!isAdmin}
                onChange={(e) => setForm({ ...form, currency_code: e.target.value })} data-testid="cfg-currency">
                <option value="">—</option>
                {CURRENCIES.map((x) => <option key={x} value={x}>{x}</option>)}
              </select>
            </div>
            <div style={{ gridColumn: "1 / -1" }}>
              <label className="jde-label">What &quot;PO Due Date&quot; means for this customer</label>
              <select className="jde-select" value={form.po_due_date_means} disabled={!isAdmin}
                onChange={(e) => setForm({ ...form, po_due_date_means: e.target.value })} data-testid="cfg-due-means">
                {DUE_MEANS.map((x) => <option key={x.v} value={x.v}>{x.l}</option>)}
              </select>
            </div>
            <div>
              <label className="jde-label">Ex-Factory Offset (days BEFORE due date)</label>
              <input className="jde-input" type="number" value={form.ex_factory_offset_days} disabled={!isAdmin}
                onChange={(e) => setForm({ ...form, ex_factory_offset_days: e.target.value })} data-testid="cfg-offset" />
            </div>
            <div>
              <label className="jde-label">Transit Days (factory → site)</label>
              <input className="jde-input" type="number" value={form.transit_days} disabled={!isAdmin}
                onChange={(e) => setForm({ ...form, transit_days: e.target.value })} data-testid="cfg-transit" />
            </div>
            <div style={{ gridColumn: "1 / -1" }}>
              <label className="jde-label">Delivery Responsibility</label>
              <select className="jde-select" value={form.delivery_responsibility} disabled={!isAdmin}
                onChange={(e) => setForm({ ...form, delivery_responsibility: e.target.value })} data-testid="cfg-resp">
                {RESP.map((x) => <option key={x.v} value={x.v}>{x.l}</option>)}
              </select>
            </div>
            <div style={{ gridColumn: "1 / -1" }}>
              <label className="jde-label">Notes</label>
              <textarea className="jde-textarea" rows={2} value={form.notes} disabled={!isAdmin}
                onChange={(e) => setForm({ ...form, notes: e.target.value })} data-testid="cfg-notes" />
            </div>
          </div>
          <div style={{ marginTop: 14, display: "flex", justifyContent: "flex-end", gap: 8 }}>
            <button className="jde-btn" onClick={onClose}>Cancel</button>
            <button className="jde-btn jde-btn-primary" onClick={save} disabled={busy} data-testid="cfg-save">
              {busy ? "Saving…" : "Save"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function CreateModal({ onClose, onSaved }) {
  const [form, setForm] = useState({
    ms_pcode: "", name: "", currency_code: "USD",
    po_due_date_means: "delivery_date", ex_factory_offset_days: 0,
    transit_days: 0, delivery_responsibility: "customer_pickup",
  });
  const [busy, setBusy] = useState(false);
  const [erp, setErp] = useState(null); // null | "looking" | {found:bool, name?, currency?}

  const lookupErp = async () => {
    const code = form.ms_pcode.trim();
    if (!code) { setErp(null); return; }
    setErp("looking");
    try {
      const r = (await api.get(`/customers/erp-lookup`, { params: { code } })).data;
      setErp(r);
      if (r.found) {
        // Auto-fill name from the ERP master (and currency if inferred) unless already typed.
        setForm((f) => ({
          ...f,
          name: f.name.trim() ? f.name : r.name,
          currency_code: r.currency || f.currency_code,
        }));
      }
    } catch {
      setErp(null);
    }
  };

  const create = async () => {
    if (!form.ms_pcode.trim() || !form.name.trim()) {
      toast.error("Customer Code and Name are required");
      return;
    }
    setBusy(true);
    try {
      await api.post(`/customers`, {
        ms_pcode: form.ms_pcode.trim(),
        name: form.name.trim(),
        currency_code: form.currency_code || null,
        date_config: {
          po_due_date_means: form.po_due_date_means,
          delivery_responsibility: form.delivery_responsibility,
          ex_factory_offset_days: Number(form.ex_factory_offset_days) || 0,
          transit_days: Number(form.transit_days) || 0,
        },
      });
      toast.success(`Customer "${form.name.trim()}" created`);
      onSaved();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Create failed");
    } finally { setBusy(false); }
  };

  return (
    <div onClick={onClose} style={{
      position: "fixed", inset: 0, background: "rgba(11, 18, 32, 0.55)",
      display: "flex", alignItems: "center", justifyContent: "center", zIndex: 100, padding: 20,
    }}>
      <div onClick={(e) => e.stopPropagation()} style={{
        background: "#fff", borderRadius: 10, width: "100%", maxWidth: 560,
        boxShadow: "0 24px 60px rgba(0,0,0,0.3)", overflow: "hidden",
      }} data-testid="customer-create-modal">
        <div className="jde-panel-header">
          <span>Add New Customer</span>
          <button className="jde-btn jde-btn-sm" onClick={onClose}><X size={11} weight="bold" /></button>
        </div>
        <div className="jde-panel-body">
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
            <div>
              <label className="jde-label">Customer Code (ERP Pcode) *</label>
              <input className="jde-input text-mono" placeholder="e.g. WMCC" value={form.ms_pcode}
                onChange={(e) => { setForm({ ...form, ms_pcode: e.target.value.toUpperCase() }); setErp(null); }}
                onBlur={lookupErp} data-testid="new-pcode" />
              {erp === "looking" && <div className="muted" style={{ fontSize: 10, marginTop: 3 }} data-testid="new-erp-status">Checking ERP…</div>}
              {erp && erp !== "looking" && erp.found && (
                <div style={{ fontSize: 10, marginTop: 3, color: "#15803D" }} data-testid="new-erp-status">
                  ✓ Found in ERP: {erp.name}
                </div>
              )}
              {erp && erp !== "looking" && !erp.found && (
                <div style={{ fontSize: 10, marginTop: 3, color: "#B45309" }} data-testid="new-erp-status">
                  Not found in ERP — check the code, or enter the name manually.
                </div>
              )}
              {erp && erp !== "looking" && erp.existing && (
                <div style={{ fontSize: 10, marginTop: 3, color: "#B91C1C", fontWeight: 700 }} data-testid="new-erp-dup">
                  ⚠ Already in OMS as &quot;{erp.existing.name}&quot; ({erp.existing.ms_pcode}) — no need to add.
                </div>
              )}
            </div>
            <div>
              <label className="jde-label">Default Currency</label>
              <select className="jde-select" value={form.currency_code}
                onChange={(e) => setForm({ ...form, currency_code: e.target.value })} data-testid="new-currency">
                {CURRENCIES.map((x) => <option key={x} value={x}>{x}</option>)}
              </select>
            </div>
            <div style={{ gridColumn: "1 / -1" }}>
              <label className="jde-label">Customer Name *</label>
              <input className="jde-input" placeholder="e.g. Wal-Mart Canada Corp" value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="new-name" />
            </div>
            <div style={{ gridColumn: "1 / -1" }}>
              <label className="jde-label">What &quot;PO Due Date&quot; means for this customer</label>
              <select className="jde-select" value={form.po_due_date_means}
                onChange={(e) => setForm({ ...form, po_due_date_means: e.target.value })} data-testid="new-due-means">
                {DUE_MEANS.map((x) => <option key={x.v} value={x.v}>{x.l}</option>)}
              </select>
            </div>
            <div>
              <label className="jde-label">Ex-Factory Offset (days)</label>
              <input className="jde-input" type="number" value={form.ex_factory_offset_days}
                onChange={(e) => setForm({ ...form, ex_factory_offset_days: e.target.value })} data-testid="new-offset" />
            </div>
            <div>
              <label className="jde-label">Transit Days (factory → site)</label>
              <input className="jde-input" type="number" value={form.transit_days}
                onChange={(e) => setForm({ ...form, transit_days: e.target.value })} data-testid="new-transit" />
            </div>
            <div style={{ gridColumn: "1 / -1" }}>
              <label className="jde-label">Delivery Responsibility</label>
              <select className="jde-select" value={form.delivery_responsibility}
                onChange={(e) => setForm({ ...form, delivery_responsibility: e.target.value })} data-testid="new-resp">
                {RESP.map((x) => <option key={x.v} value={x.v}>{x.l}</option>)}
              </select>
            </div>
          </div>
          <p className="muted" style={{ fontSize: 11, marginTop: 10 }}>
            The ERP Code links this customer to its POs, dispatches and invoices. Manually-added
            customers are protected from the ERP sync&apos;s auto-cleanup.
          </p>
          <div style={{ marginTop: 14, display: "flex", justifyContent: "flex-end", gap: 8 }}>
            <button className="jde-btn" onClick={onClose}>Cancel</button>
            <button className="jde-btn jde-btn-primary" onClick={create} disabled={busy} data-testid="new-create-btn">
              {busy ? "Creating…" : "Create Customer"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
